import { useMemo, useState } from "react";
import { useQueries, useQuery } from "react-query";
import { FileDown, FileText } from "lucide-react";
import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Select,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Wrap,
  useToast,
} from "@chakra-ui/react";
import { useAuth } from "../../services/auth/AuthProvider";
import { MultiSelect } from "../../components/MultiSelect";
import { DateRangePicker } from "../statistics/DateRangePicker";
import { type Period, rangeFor } from "../statistics/period";
import { getInvoices, invoiceMs } from "../../services/api/invoices";
import { getBooksForPublisher } from "../../services/api/books";
import { getPublishers } from "../../services/api/publishers";
import type { BalanceRow, ReportLabels } from "./balanceReport";

const TODAY = new Date().toISOString().slice(0, 10);
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const PLATFORM_OPTIONS = [
  { value: "iOS", label: "iOS" },
  { value: "Android", label: "Android" },
  { value: "Web", label: "Web" },
];

interface Applied {
  startDate: string;
  endDate: string;
  startMs: number;
  endMs: number;
  scopedNames: string[]; // publishers whose books we load
  publishersParam?: string; // server filter (undefined = all, superadmin only)
  headerPublishers: string;
}

export function BalancePage() {
  const toast = useToast();
  const { publishingHouse, isSuperadmin } = useAuth();
  const ownName = publishingHouse?.name ?? "";
  const ownPct = publishingHouse?.revenuePercent ?? 1;

  const [period, setPeriod] = useState<Period>("currentMonth");
  const [customStart, setCustomStart] = useState(`${TODAY.slice(0, 7)}-01`);
  const [customEnd, setCustomEnd] = useState(TODAY);
  const [selectedPublishers, setSelectedPublishers] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [applied, setApplied] = useState<Applied | null>(null);

  const publishersQuery = useQuery(["publishers"], getPublishers, { enabled: isSuperadmin });
  const allPublishers = useMemo(() => publishersQuery.data ?? [], [publishersQuery.data]);
  const pctByName = useMemo(() => {
    const m: Record<string, number> = {};
    if (isSuperadmin) allPublishers.forEach((p) => (m[p.name] = p.revenuePercent));
    else m[ownName] = ownPct;
    return m;
  }, [isSuperadmin, allPublishers, ownName, ownPct]);

  const invoicesQuery = useQuery(
    ["balance-invoices", applied?.startDate, applied?.endDate, applied?.publishersParam],
    () =>
      getInvoices({
        startDate: applied!.startDate,
        endDate: applied!.endDate,
        publishers: applied?.publishersParam,
      }),
    { enabled: !!applied, keepPreviousData: true },
  );

  // Books for every scoped publisher → id→book map for invoice enrichment.
  const bookQueries = useQueries(
    (applied?.scopedNames ?? []).map((name) => ({
      queryKey: ["books", name],
      queryFn: () => getBooksForPublisher(name),
      enabled: !!applied,
    })),
  );
  const booksLoading = bookQueries.some((q) => q.isLoading);
  const bookMap = useMemo(() => {
    const m: Record<string, { name: string; isbn: string; authors: string }> = {};
    bookQueries.forEach((q) =>
      (q.data ?? []).forEach((b) => {
        m[b.id] = { name: b.name, isbn: b.isbn, authors: (b.authors ?? []).join(", ") };
      }),
    );
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookQueries.map((q) => q.dataUpdatedAt).join(",")]);

  // Enriched, valid rows (book resolved). pct resolved per invoice's house.
  const allRows = useMemo<BalanceRow[]>(() => {
    const invoices = invoicesQuery.data ?? [];
    return invoices.map((inv) => {
      const book = bookMap[inv.bookId];
      const price = parseFloat(String(inv.priceInUah ?? 0)) || 0;
      const pct = pctByName[inv.publishingHouse ?? ""] ?? (isSuperadmin ? 1 : ownPct);
      return {
        transactionMs: invoiceMs(inv.updateDate),
        bookName: book?.name || inv.bookId,
        isbn: book?.isbn ?? "",
        authors: book?.authors ?? "",
        priceInUah: price,
        revenue: price * pct,
        platform: inv.platform ?? "",
        publishingHouse: inv.publishingHouse ?? "",
      };
    });
  }, [invoicesQuery.data, bookMap, pctByName, isSuperadmin, ownPct]);

  const rows = useMemo(
    () =>
      selectedPlatforms.length === 0
        ? allRows
        : allRows.filter((r) => selectedPlatforms.includes(r.platform)),
    [allRows, selectedPlatforms],
  );

  const total = useMemo(() => rows.reduce((s, r) => s + r.revenue, 0), [rows]);

  function showResults() {
    if (period === "custom" && customStart > customEnd) {
      toast({ description: "Невірний діапазон дат", status: "error" });
      return;
    }
    const range = rangeFor(period, customStart, customEnd);
    const scopedNames = isSuperadmin
      ? selectedPublishers.length
        ? selectedPublishers
        : allPublishers.map((p) => p.name)
      : [ownName];
    const headerPublishers = selectedPublishers.length
      ? selectedPublishers.join(", ")
      : isSuperadmin
        ? "Всі видавництва"
        : ownName;
    setSelectedPlatforms([]);
    setApplied({
      startDate: range.startIso,
      endDate: range.endIso,
      startMs: new Date(range.startIso).getTime(),
      endMs: new Date(range.endIso).getTime(),
      scopedNames,
      publishersParam: isSuperadmin
        ? selectedPublishers.length
          ? selectedPublishers.join(",")
          : undefined
        : ownName,
      headerPublishers,
    });
  }

  const reportLabels: ReportLabels = {
    report: "Звіт",
    salesReport: "Звіт продажів",
    isbn: "ISBN",
    name: "Назва",
    authors: "Автор",
    price: "Фактична ціна продажу книжки (грн)",
    count: "Кількість",
    royalty: "Роялті",
    total: "Всього",
    transactionDate: "Дата транзакції",
    bookName: "Книжка",
    revenue: "Виручка видавництва (грн)",
    sumForPayment: "Сума до виплати",
  };

  async function exportXlsx() {
    const mod = await import("./balanceReport");
    mod.downloadXlsx(rows, total, reportLabels);
  }
  async function exportPdf() {
    const mod = await import("./balanceReport");
    await mod.downloadPdf(rows, total, {
      publishers: applied?.headerPublishers ?? "",
      startMs: applied?.startMs ?? 0,
      endMs: applied?.endMs ?? 0,
    }, reportLabels);
  }

  const loading = invoicesQuery.isLoading || booksLoading;
  const hasResults = !!applied && !loading && rows.length > 0;

  return (
    <Flex direction="column" h="100%" overflow="hidden">
      <Flex
        as="header"
        flex="0 0 auto"
        align="center"
        bg="white"
        borderBottom="1px solid"
        borderColor="blackAlpha.100"
        px={{ base: 4, md: 6 }}
        py={{ base: 3, md: 4 }}
      >
        <Heading size={{ base: "md", md: "lg" }} fontWeight="medium" letterSpacing="wide" textTransform="uppercase">
          {"Баланс"}
        </Heading>
      </Flex>

      <Box flex="0 0 auto" px={{ base: 4, md: 6 }} pt={{ base: 4, md: 6 }} pb={4}>
        <Wrap spacing={3} align="flex-end">
          <FormControl w="fit-content">
            <FormLabel fontSize="xs">{"Період"}</FormLabel>
            <Select value={period} onChange={(e) => setPeriod(e.target.value as Period)} size="sm">
              <option value="currentMonth">{"Поточний місяць"}</option>
              <option value="previousMonth">{"Попередній місяць"}</option>
              <option value="currentYear">{"Поточний рік"}</option>
              <option value="previousYear">{"Попередній рік"}</option>
              <option value="custom">{"Інша дата"}</option>
            </Select>
          </FormControl>

          {period === "custom" && (
            <FormControl w="fit-content">
              <FormLabel fontSize="xs">{"Інша дата"}</FormLabel>
              <DateRangePicker
                start={customStart}
                end={customEnd}
                placeholder={"Обрати діапазон"}
                onChange={(s, e) => {
                  setCustomStart(s);
                  setCustomEnd(e);
                }}
              />
            </FormControl>
          )}

          {isSuperadmin && (
            <FormControl w="fit-content">
              <FormLabel fontSize="xs">{"Видавництва"}</FormLabel>
              <MultiSelect
                label={"Видавництва"}
                options={allPublishers.map((p) => ({ value: p.name, label: p.name }))}
                value={selectedPublishers}
                onChange={setSelectedPublishers}
                searchPlaceholder={"Пошук"}
                minW="220px"
              />
            </FormControl>
          )}

          <FormControl w="fit-content">
            <FormLabel fontSize="xs">{"Платформи"}</FormLabel>
            <MultiSelect
              label={"Платформи"}
              options={PLATFORM_OPTIONS}
              value={selectedPlatforms}
              onChange={setSelectedPlatforms}
            />
          </FormControl>

          <Button onClick={showResults} isLoading={loading} isDisabled={isSuperadmin && publishersQuery.isLoading}>
            {"Показати результати"}
          </Button>
        </Wrap>
      </Box>

      {!applied ? (
        <Center flex="1">
          <Text color="gray.500">{"Показати результати"}</Text>
        </Center>
      ) : loading ? (
        <Center flex="1">
          <Spinner color="brand.orange" />
        </Center>
      ) : rows.length === 0 ? (
        <Center flex="1">
          <Text color="gray.500">{"Немає результатів"}</Text>
        </Center>
      ) : (
        <>
          {/* Payout total + export — pinned */}
          <Box flex="0 0 auto" px={{ base: 4, md: 6 }} pb={4}>
            <Flex
              align="center"
              justify="space-between"
              wrap="wrap"
              gap={4}
              bg="white"
              border="1px solid"
              borderColor="blackAlpha.100"
              borderRadius="lg"
              boxShadow="sm"
              p={4}
            >
              <Stat flex="0 0 auto">
                <StatLabel color="gray.500">{"Сума до виплати"}</StatLabel>
                <StatNumber color="brand.orange">{round2(total)} грн.</StatNumber>
              </Stat>
              {/* `gap` + wrap (not HStack) so the two export buttons share the
                  row on mobile instead of overflowing the card. */}
              <Flex gap={3} flexWrap="wrap" w={{ base: "full", md: "auto" }}>
                <Button
                  variant="outline"
                  flex={{ base: "1 1 8rem", md: "0 0 auto" }}
                  leftIcon={<Icon as={FileDown} boxSize={4} />}
                  onClick={exportXlsx}
                  isDisabled={!hasResults}
                >
                  {"Звіт XLSX"}
                </Button>
                <Button
                  variant="outline"
                  flex={{ base: "1 1 8rem", md: "0 0 auto" }}
                  leftIcon={<Icon as={FileText} boxSize={4} />}
                  onClick={exportPdf}
                  isDisabled={!hasResults}
                >
                  {"Звіт PDF"}
                </Button>
              </Flex>
            </Flex>
          </Box>

          {/* Only the table scrolls */}
          <Box
            flex="1 1 auto"
            minH={0}
            overflowY="auto"
            overflowX="auto"
            mx={{ base: 4, md: 6 }}
            mb={{ base: 4, md: 6 }}
            bg="white"
            border="1px solid"
            borderColor="blackAlpha.100"
            borderRadius="lg"
            boxShadow="sm"
          >
            <Table size="sm" variant="simple">
              <Thead position="sticky" top={0} zIndex={1} bg="gray.50">
                <Tr>
                  <Th whiteSpace="normal">{"Дата транзакції"}</Th>
                  <Th whiteSpace="normal">{"Книжка"}</Th>
                  <Th isNumeric whiteSpace="normal">{"Фактична ціна продажу книжки (грн)"}</Th>
                  <Th isNumeric whiteSpace="normal">{"Виручка видавництва (грн)"}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((r, i) => (
                  <Tr key={i}>
                    <Td whiteSpace="nowrap">
                      {r.transactionMs
                        ? new Date(r.transactionMs).toLocaleDateString("uk-UA")
                        : "—"}
                    </Td>
                    <Td>{r.bookName}</Td>
                    <Td isNumeric>{r.priceInUah}</Td>
                    <Td isNumeric>{round2(r.revenue)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Flex>
  );
}
